import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { errorInfo, isDuplicateRefusal, isOverrideValid, newIdempotencyKey } from "../lib/collaborators";
import useCollabReferentials from "../hooks/useCollabReferentials";
import useSkillsReferentials from "../hooks/useSkillsReferentials";
import JobFunctionsField from "../components/JobFunctionsField";
import CollaboratorPicker from "../components/CollaboratorPicker";
import DuplicateNotice from "../components/DuplicateNotice";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lightbulb, ShieldCheck } from "lucide-react";

const NONE = "__none__";
const today = () => new Date().toISOString().slice(0, 10);

export default function CollaboratorFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // arriving from the Users page: the fiche is created for that account and linked to it in the same step (administrator only)
  const fromUser = useLocation().state?.fromUser || null;
  const refs = useCollabReferentials();
  const skillRefs = useSkillsReferentials();
  // Une clé par ouverture du formulaire : une double soumission ne crée jamais deux fiches, un refus ne la consomme pas.
  const idempotencyKey = useRef(newIdempotencyKey());
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: fromUser ? fromUser.name.trim().split(/\s+/).slice(0, -1).join(" ") : "",
    last_name: fromUser ? fromUser.name.trim().split(/\s+/).slice(-1)[0] : "",
    relation_type: "", job_title: "", job_function_ids: [], status: "pre_registered", entry_date: "",
    service_id: "", manager_id: "", manager_user_id: "", manager_label: "", top_level: false, zone_ids: [], email: fromUser?.email || "", phone: fromUser?.whatsapp || "", whatsapp: fromUser?.whatsapp || "",
  });
  const [refusal, setRefusal] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState("");

  const set = (key) => (value) => {
    setRefusal(null);
    setConfirmed(false);
    setForm((f) => ({ ...f, [key]: value }));
  };
  const setInput = (key) => (e) => set(key)(e.target.value);
  const toggleZone = (code, on) => set("zone_ids")(on ? [...form.zone_ids, code] : form.zone_ids.filter((z) => z !== code));

  const activeTypes = refs.relation_types.filter((r) => r.active);
  const activeServices = refs.services.filter((s) => s.active);
  const activeZones = refs.zones.filter((z) => z.active);
  const relationType = form.relation_type || (activeTypes.find((r) => r.code === "salarie") || activeTypes[0])?.code || "";

  const buildBody = () => {
    const contacts = {};
    for (const key of ["email", "phone", "whatsapp"]) if (form[key].trim()) contacts[key] = form[key].trim();
    const body = {
      first_name: form.first_name.trim() || null, last_name: form.last_name.trim() || null, relation_type: relationType, job_title: form.job_title.trim() || null, job_function_ids: form.job_function_ids,
      status: form.status, service_id: form.service_id || null, manager_id: form.manager_id || null, manager_user_id: form.manager_user_id || null, zone_ids: form.zone_ids, contacts,
    };
    if (isAdmin && form.top_level) body.top_level = true;
    if (form.status === "active") body.entry_date = form.entry_date;
    if (fromUser) body.link_user_id = fromUser.id;
    if (refusal?.can_override && confirmed) {
      body.confirm_duplicates = true;
      body.override_reason = reason.trim();
    }
    return body;
  };

  const canSubmit =
    !saving && (form.first_name.trim() || form.last_name.trim()) && relationType &&
    (form.status !== "active" || (form.entry_date && form.entry_date <= today())) &&
    (!refusal?.can_override || !confirmed || isOverrideValid(confirmed, reason));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/collaborators", buildBody(), { headers: { "Idempotency-Key": idempotencyKey.current } });
      toast.success(data.replayed ? "Cette fiche existe déjà : elle a été ouverte" : "Collaborateur ajouté");
      navigate(`/collaborateurs/${data.id}`);
    } catch (err) {
      const info = errorInfo(err);
      if (isDuplicateRefusal(info)) {
        setRefusal(info.data);
        if (info.code === "override_reason_required") toast.error(info.data.msg);
      } else {
        toast.error(errorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="collab-form-page">
      <Link to="/collaborateurs" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Retour aux collaborateurs
      </Link>
      {fromUser && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="from-user-banner">
          Cette fiche sera rattachée au compte CRM de <strong>{fromUser.name}</strong> ({fromUser.email}) dès son enregistrement.
        </div>
      )}
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Nouveau collaborateur</h1>
        <p className="text-sm text-slate-500">Enregistrez une personne qui travaille avec Jamora. Elle n'a pas besoin d'un compte CRM.</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Identité</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="collab-first-name">Prénom</Label>
                <Input id="collab-first-name" data-testid="input-collab-first-name" className="text-base md:text-sm" value={form.first_name} onChange={setInput("first_name")} maxLength={100} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="collab-last-name">Nom</Label>
                <Input id="collab-last-name" data-testid="input-collab-last-name" className="text-base md:text-sm" value={form.last_name} onChange={setInput("last_name")} maxLength={100} />
              </div>
              <p className="text-xs text-slate-500 md:col-span-2" data-testid="collab-name-hint">Indiquez au moins un prénom ou un nom.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="collab-job-title">Fonction</Label>
              <Input id="collab-job-title" data-testid="input-collab-job-title" className="text-base md:text-sm" value={form.job_title} onChange={setInput("job_title")} maxLength={120}
                placeholder="Ex. : Formateur POS, Commercial terrain, Développeur" />
              <p className="text-xs text-slate-500">Ce que fait la personne chez Jamora, en quelques mots.</p>
            </div>
            {skillRefs.status === "ready" && (
              <div className="space-y-2">
                <Label>Fonctions métier</Label>
                <JobFunctionsField testid="collab-job-functions" functions={skillRefs.functions} value={form.job_function_ids} onChange={set("job_function_ids")} />
                <p className="text-xs text-slate-500">Une personne peut en avoir plusieurs. Elles ne donnent aucun accès au CRM.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Type de relation *</Label>
              <Select value={relationType} onValueChange={set("relation_type")} disabled={refs.status !== "ready"}>
                <SelectTrigger data-testid="select-collab-type"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{activeTypes.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Coordonnées professionnelles</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="collab-email">Email</Label>
                <Input id="collab-email" type="email" inputMode="email" data-testid="input-collab-email" className="text-base md:text-sm" value={form.email} onChange={setInput("email")} placeholder="prenom.nom@jamora.sn" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="collab-phone">Téléphone</Label>
                <Input id="collab-phone" type="tel" inputMode="tel" data-testid="input-collab-phone" className="text-base md:text-sm" value={form.phone} onChange={setInput("phone")} placeholder="+221 77 123 45 67" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="collab-whatsapp">WhatsApp</Label>
                <Input id="collab-whatsapp" type="tel" inputMode="tel" data-testid="input-collab-whatsapp" className="text-base md:text-sm" value={form.whatsapp} onChange={setInput("whatsapp")} placeholder="+221 77 123 45 67" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Uniquement des coordonnées professionnelles. Un même numéro ou email peut être partagé : il vous sera demandé de le confirmer.</p>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Organisation</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Service</Label>
                <Select value={form.service_id || NONE} onValueChange={(v) => set("service_id")(v === NONE ? "" : v)}>
                  <SelectTrigger data-testid="select-collab-service"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Aucun service</SelectItem>
                    {activeServices.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Responsable</Label>
                <CollaboratorPicker
                  testid="collab-manager" value={form.manager_id || form.manager_user_id} label={form.manager_label} allowAccounts disabled={form.top_level}
                  onChange={(id, name, kind) => {
                    set("manager_id")(kind === "collaborator" ? id || "" : "");
                    setForm((f) => ({ ...f, manager_id: kind === "collaborator" ? id || "" : "", manager_user_id: kind === "account" ? id : "", manager_label: name || "" }));
                  }}
                />
              </div>
              {isAdmin && (
                <label className="flex items-start gap-2 text-sm md:col-span-2" data-testid="collab-top-level-label">
                  <Checkbox className="mt-0.5" checked={form.top_level} data-testid="collab-top-level"
                    onCheckedChange={(v) => setForm((f) => ({ ...f, top_level: v === true, ...(v === true ? { manager_id: "", manager_user_id: "", manager_label: "" } : {}) }))} />
                  <span>
                    Sommet de l'organisation
                    <span className="block text-xs text-slate-500">Cette personne n'a pas de responsable. Plusieurs personnes peuvent être au sommet, sans lien entre elles.</span>
                  </span>
                </label>
              )}
            </div>
            {activeZones.length > 0 && (
              <div className="space-y-2">
                <Label>Zones</Label>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {activeZones.map((z) => (
                    <label key={z.code} className="flex items-center gap-2 text-sm min-h-[32px]">
                      <Checkbox checked={form.zone_ids.includes(z.code)} onCheckedChange={(v) => toggleZone(z.code, v === true)} data-testid={`zone-${z.code}`} />
                      {z.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Début de la collaboration</div>
            <p className="text-xs text-slate-500 -mt-2">Choisissez « Actif » si la personne travaille déjà avec vous (indiquez alors sa date d'entrée), ou « Préinscrit » si elle n'a pas encore commencé.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statut au départ</Label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger data-testid="select-collab-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre_registered">Préinscrit (pas encore commencé)</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.status === "active" && (
                <div className="space-y-2">
                  <Label htmlFor="collab-entry-date">Date d'entrée *</Label>
                  <Input id="collab-entry-date" type="date" max={today()} data-testid="input-collab-entry-date" className="text-base md:text-sm" value={form.entry_date} onChange={setInput("entry_date")} />
                </div>
              )}
            </div>
          </Card>

          <DuplicateNotice refusal={refusal} confirmed={confirmed} reason={reason} onConfirmedChange={setConfirmed} onReasonChange={setReason} />

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/collaborateurs")} className="h-11 sm:h-10">Annuler</Button>
            <Button type="submit" disabled={!canSubmit} data-testid="btn-collab-submit" className="h-11 sm:h-10">
              {saving && <Loader2 size={14} className="animate-spin mr-1.5" />}
              {refusal?.can_override && confirmed ? "Enregistrer malgré l'alerte" : "Enregistrer"}
            </Button>
          </div>
        </div>

        <div className="space-y-4 lg:sticky lg:top-4">
          <Card className="p-4 space-y-2 bg-slate-50 border-slate-200">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Lightbulb size={15} className="text-jamora" /> Fiche et compte sont distincts</div>
            <p className="text-xs text-slate-600">Une personne peut figurer dans l'annuaire sans pouvoir se connecter. Le rattachement à un compte CRM est fait ensuite par un administrateur.</p>
          </Card>
          <Card className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><ShieldCheck size={15} className="text-jamora" /> Données minimales</div>
            <p className="text-xs text-slate-600">Seuls le nom, les coordonnées professionnelles et l'organisation sont enregistrés. Aucune donnée privée n'est demandée.</p>
          </Card>
        </div>
      </form>
    </div>
  );
}
